<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import TextInput from '../forms/inputs/text-input.vue';

const emit = defineEmits(['update:modelValue', 'update:buyNowPrice', 'update:minimumBid', 'update:evalPrice']);
const errorMessage = ref('');

const props = defineProps({
    modelValue: {
        type: String,
        default: 'buy_now_bidding'
    },
    buyNowPrice: {
        type: [Number, String],
        default: ''
    },
    evalPrice: {
        type: [Number, String],
        default: ''
    },
    minimumBid: {
        type: [Number, String],
        default: ''
    },
    isEvalDataset: {
        type: Boolean,
        default: false
    }
});

const selectedType = ref(props.modelValue);
const buyNowPrice = ref(props.buyNowPrice);
const minimumBid = ref(props.minimumBid);

const listingTypes = [
    {
        id: 'buy_now_bidding',
        label: 'Buy Now + Bidding',
        description: 'Choose how buyers can acquire your dataset.',
        tooltip: 'Instant purchase and offers. Buyers may pay the Buy Now price immediately or submit bids at/above your Minimum Bid.'
    },
    {
        id: 'buy_now',
        label: 'Buy Now Only',
        description: 'Choose how buyers can acquire your dataset.',
        tooltip: 'Fixed-price listing. The dataset is available for immediate purchase at the Buy Now price. Bids are disabled.'
    },
    {
        id: 'bidding',
        label: 'Bidding Only',
        description: 'Choose how buyers can acquire your dataset.',
        tooltip: 'Auction-style listing. No fixed price; buyers must place bids at/above your Minimum Bid. You choose which bid to accept.'
    }
];

watch(() => props.modelValue, (newValue) => {
    selectedType.value = newValue;
});

watch(() => props.buyNowPrice, (newValue) => {
    buyNowPrice.value = newValue;
});

watch(() => props.minimumBid, (newValue) => {
    minimumBid.value = newValue;
});

const handleTypeSelection = (type) => {
    selectedType.value = type;
    emit('update:modelValue', type);

    if (type === 'buy_now') {
        minimumBid.value = '';
        emit('update:minimumBid', '');
    } else if (type === 'bidding') {
        buyNowPrice.value = '';
        emit('update:buyNowPrice', '');
    }
};

const handleBuyNowPriceUpdate = (value) => {
    const numericValue = value === '' ? '' : parseFloat(value);
    buyNowPrice.value = numericValue;
    
    // Validar se o preço mínimo de bid é maior que o buy now
    if (minimumBid.value && numericValue && minimumBid.value > numericValue) {
        errorMessage.value = 'The minimum bid cannot be greater than the buy now price';
        minimumBid.value = numericValue;
        emit('update:minimumBid', numericValue);
    } else {
        errorMessage.value = '';
    }
    
    emit('update:buyNowPrice', numericValue);
};

const handleMinimumBidUpdate = (value) => {
    const numericValue = value === '' ? '' : parseFloat(value);
    
    if (buyNowPrice.value && numericValue && numericValue >= buyNowPrice.value) {
        errorMessage.value = 'The minimum bid cannot be greater than the buy now price';
        minimumBid.value = buyNowPrice.value;
        emit('update:minimumBid', buyNowPrice.value);
        return;
    }
    
    errorMessage.value = '';
    minimumBid.value = numericValue;
    emit('update:minimumBid', numericValue);
};

const handleEvalPriceUpdate = (value) => {
    const numericValue = value === '' ? '' : parseFloat(value);
    emit('update:evalPrice', numericValue);
};


</script>

<template>
    <div class="listing-type-selection">
        <div class="listing-type-container">
            <h3 class="listing-type-title" data-testid="listing-type-title">Listing Type</h3>
            <p class="listing-type-description">Choose how buyers can acquire your dataset.</p>
            
            <div class="listing-type-options">
                <div
                    v-for="type in listingTypes"
                    :key="type.id"
                    class="listing-type-option"
                    :class="{ 'selected': selectedType === type.id }"
                    @click="handleTypeSelection(type.id)"
                    :data-testid="type.id"
                >
                    <div class="radio-button">
                        <div class="radio-inner" v-if="selectedType === type.id"></div>
                    </div>
                    <div class="option-content">
                        <span class="option-label">{{ type.label }}</span>
                        <span class="info-icon" :class="{ 'selected': selectedType === type.id }">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                            </svg>
                            <span class="tooltip">{{ type.tooltip }}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div class="price-container">
            <div class="price-section" v-if="props.isEvalDataset">
                <h3 class="price-title">Price Per Eval</h3>
                <div class="price-input">
                    <TextInput 
                        type="number" 
                        :modelValue="evalPrice"
                        @update:modelValue="handleEvalPriceUpdate"
                        :placeholder="0.00" 
                        :required="true"
                        min="0"
                        step="0.01"
                    />
                    <span class="currency">USD</span>
                </div>
            </div>

            <div class="price-section" v-if="selectedType !== 'bidding'">
                <h3 class="price-title">Core Dataset Price (Buy Now)</h3>
                <div class="price-input">
                    <TextInput 
                        type="number" 
                        :modelValue="buyNowPrice"
                        @update:modelValue="handleBuyNowPriceUpdate"
                        :placeholder="0.00" 
                        :required="true"
                        min="0"
                        step="0.01"
                        dataTestId="price-input"
                    />
                    <span class="currency">USD</span>
                </div>
            </div>

            <div class="price-section h-20" v-if="selectedType !== 'buy_now'">
                <h3 class="price-title">Core Dataset Minimum Bid</h3>
                <div class="price-input">
                    <TextInput 
                        type="number" 
                        :modelValue="minimumBid"
                        @update:modelValue="handleMinimumBidUpdate"
                        :placeholder="0.00" 
                        :required="true"
                        min="0"
                        step="0.01"
                        dataTestId="minimum-bid-input"
                    />
                    <span class="currency">USD</span>
                </div>
                <div v-if="errorMessage">
                    <div class="error-message">
                        <i class="uil uil-exclamation-circle"></i>
                        {{ errorMessage }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="sass">
.listing-type-selection {
    @apply flex flex-col md:flex-row gap-6;
}

.listing-type-container {
    @apply bg-echoes-background-black rounded-[21px] px-6 py-5 overflow-hidden border border-echoes-border-gray w-full
}

.listing-type-title {
    @apply font-semibold text-white/80 text-lg mb-2 relative pb-4 mb-4;
}

.listing-type-title::after {
    content: '';
    @apply absolute bottom-0 left-0 right-0 h-px bg-echoes-background-gray;
}

.listing-type-description {
    @apply text-white/80 text-sm mb-4;
}

.listing-type-options {
    @apply space-y-2;
}

.listing-type-option {
    @apply flex items-center gap-3 cursor-pointer transition-all duration-200;
}

.radio-button {
    @apply w-6 h-6 rounded-full border-2 border-echoes-border-gray flex items-center justify-center transition-all duration-200;
}

.listing-type-option.selected .radio-button {
    @apply border-echoes-primary;
}

.radio-inner {
    @apply w-3 h-3 rounded-full bg-echoes-primary;
}

.option-content {
    @apply flex items-center gap-2;
}

.option-label {
    @apply text-white/80 text-sm font-medium;
}

.listing-type-option.selected .option-label {
    @apply text-white/80;
}

.info-icon {
    @apply text-white/80 hover:text-echoes-primary transition-colors relative;
}

.info-icon.selected {
    @apply text-echoes-primary;
}

.tooltip {
    @apply absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 text-xs font-semibold text-white/80 bg-echoes-background-black rounded-lg whitespace-normal w-max max-w-[300px];
    visibility: hidden;
    opacity: 0;
    transition: all 0.2s ease-in-out;
    z-index: 50;
}

.tooltip::after {
    content: '';
    @apply absolute left-1/2 -translate-x-1/2 -bottom-1 border-4 border-transparent border-t-gray-900;
}

.info-icon:hover .tooltip {
    visibility: visible;
    opacity: 1;
   
}

.price-container {
    @apply bg-echoes-background-black rounded-[21px] px-6 py-5 overflow-hidden border border-echoes-border-gray w-full
}

.price-section {
    @apply mb-2 last:mb-0;
}

.price-title {
    @apply text-white/80 text-sm font-semibold mb-2;
}

.price-input {
    @apply relative;
}

.currency {
    @apply absolute right-4 top-1/2 -translate-y-1/2 text-white/80;
}

.error-message {
    @apply text-red-400 rounded-lg text-xs mt-2;
}
</style> 